import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Phone, MessageSquare, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Clock, Calendar, User, X, ChevronDown, ChevronUp, Volume2, Voicemail,
  RefreshCw, AlertTriangle, FileText, Loader2
} from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

// Format duration in seconds to readable format
const formatDuration = (seconds) => {
  if (!seconds) return "-";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

// Format datetime
const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format date only
const formatDate = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleDateString('fr-FR');
};

// Format time only
const formatTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// Get call state color and icon
const getCallStateInfo = (call) => {
  const state = call.last_state;
  const isAnswered = call.is_answered;
  const direction = call.direction;
  
  if (isAnswered && state === "ANSWERED") {
    return {
      icon: direction === "out" ? PhoneOutgoing : PhoneIncoming,
      color: "text-green-400",
      bgColor: "bg-green-500/20",
      label: direction === "out" ? "Sortant" : "Entrant"
    };
  }
  
  if (state === "VOICEMAIL" || state === "VOICEMAIL_ANSWERED") {
    return {
      icon: Voicemail,
      color: "text-purple-400",
      bgColor: "bg-purple-500/20",
      label: "Messagerie"
    };
  }
  
  if (call.is_nrp || state === "MISSED" || state === "FAILED" || state === "CANCELLED") {
    return {
      icon: PhoneMissed,
      color: "text-red-400",
      bgColor: "bg-red-500/20",
      label: call.is_nrp ? "NRP" : "Manqué"
    };
  }
  
  return {
    icon: Phone,
    color: "text-slate-400",
    bgColor: "bg-slate-500/20",
    label: state || "Inconnu"
  };
};

function CallItem({ call, expanded, onToggle }) {
  const stateInfo = getCallStateInfo(call);
  const StateIcon = stateInfo.icon;
  
  return (
    <div className={`border-b border-slate-700 ${call.is_nrp ? 'bg-red-500/5' : ''}`}>
      <div 
        className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-700/30"
        onClick={onToggle}
      >
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full ${stateInfo.bgColor} flex items-center justify-center flex-shrink-0`}>
          <StateIcon className={`w-5 h-5 ${stateInfo.color}`} />
        </div>
        
        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${stateInfo.bgColor} ${stateInfo.color}`}>
              {stateInfo.label}
            </span>
            {call.is_nrp && (
              <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-600 text-white">
                NRP
              </span>
            )}
            <span className="text-xs text-slate-500">{call.crm_telepro || call.ringover_user_name || "?"}</span>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm">
            <span className="text-white">{formatDate(call.start_time)}</span>
            <span className="text-slate-400">{formatTime(call.start_time)}</span>
            {call.is_answered && (
              <span className="text-green-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDuration(call.incall_duration)}
              </span>
            )}
          </div>
        </div>
        
        {/* Expand button */}
        <div className="text-slate-500">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>
      
      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 pl-16 space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-slate-500">De:</span>
              <span className="text-white ml-2">{call.from_number_raw || call.from_number || "-"}</span>
            </div>
            <div>
              <span className="text-slate-500">Vers:</span>
              <span className="text-white ml-2">{call.to_number_raw || call.to_number || "-"}</span>
            </div>
            <div>
              <span className="text-slate-500">Durée sonnerie:</span>
              <span className="text-white ml-2">{formatDuration(call.ringing_duration)}</span>
            </div>
            <div>
              <span className="text-slate-500">Durée totale:</span>
              <span className="text-white ml-2">{formatDuration(call.total_duration)}</span>
            </div>
            <div>
              <span className="text-slate-500">État:</span>
              <span className="text-white ml-2">{call.last_state}</span>
            </div>
            <div>
              <span className="text-slate-500">Contact:</span>
              <span className="text-white ml-2">{call.contact_name || "-"}</span>
            </div>
          </div>
          
          {/* Audio links */}
          <div className="flex gap-3 mt-2">
            {call.record_url && (
              <a 
                href={call.record_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
              >
                <Volume2 className="w-4 h-4" />
                Écouter l'enregistrement
              </a>
            )}
            {call.voicemail_url && (
              <a 
                href={call.voicemail_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-purple-400 hover:text-purple-300"
              >
                <Voicemail className="w-4 h-4" />
                Écouter le message
              </a>
            )}
          </div>
          
          {/* Bouton transcription avec affichage intégré (résumé + transcription complète) */}
          {call.record_url && (
            <TranscriptionButton 
              cdrId={call.cdr_id} 
              existingTranscription={call.transcription}
              existingSummary={call.ai_summary}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Composant pour le bouton de transcription
function TranscriptionButton({ cdrId, existingTranscription, existingSummary, onTranscribed }) {
  const [loading, setLoading] = useState(false);
  const [transcription, setTranscription] = useState(existingTranscription);
  const [aiSummary, setAiSummary] = useState(existingSummary);
  const [error, setError] = useState(null);
  const [showFull, setShowFull] = useState(false);

  const handleTranscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/ringover/call/${cdrId}/transcribe`);
      if (response.data.success) {
        setTranscription(response.data.transcription);
        setAiSummary(response.data.ai_summary);
        if (onTranscribed) onTranscribed(response.data.transcription, response.data.ai_summary);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de transcription");
    } finally {
      setLoading(false);
    }
  };

  if (transcription) {
    return (
      <div className="mt-2 space-y-2">
        {/* AI Summary */}
        {aiSummary && (
          <div className="p-2 bg-blue-500/10 border border-blue-500/30 rounded">
            <span className="text-blue-400 text-xs font-medium">🤖 Résumé IA:</span>
            <p className="text-white text-sm mt-1">{aiSummary}</p>
          </div>
        )}
        
        {/* Full transcription toggle */}
        <button 
          onClick={() => setShowFull(!showFull)}
          className="flex items-center gap-1 text-emerald-400 text-sm hover:text-emerald-300"
        >
          <FileText className="w-4 h-4" />
          {showFull ? "Masquer transcription complète" : "Voir transcription complète"}
        </button>
        {showFull && (
          <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg max-h-60 overflow-y-auto">
            <p className="text-white text-sm whitespace-pre-wrap">{transcription}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={handleTranscribe}
        disabled={loading}
        className="flex items-center gap-1 text-amber-400 hover:text-amber-300 disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Transcription en cours...
          </>
        ) : (
          <>
            <FileText className="w-4 h-4" />
            Transcrire
          </>
        )}
      </button>
      {error && (
        <p className="text-red-400 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

function SmsItem({ sms }) {
  const isOutbound = sms.direction === "out";
  
  return (
    <div className={`p-3 ${isOutbound ? 'pl-8' : 'pr-8'}`}>
      <div className={`p-3 rounded-lg ${isOutbound ? 'bg-blue-600/20 ml-auto' : 'bg-slate-700'} max-w-[80%] ${isOutbound ? 'ml-auto' : ''}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs ${isOutbound ? 'text-blue-400' : 'text-slate-400'}`}>
            {isOutbound ? '📤 Envoyé' : '📥 Reçu'}
          </span>
          <span className="text-xs text-slate-500">{formatDateTime(sms.timestamp)}</span>
        </div>
        <p className="text-white text-sm">{sms.content}</p>
        <div className="text-xs text-slate-500 mt-1">
          {sms.crm_telepro || "Système"}
        </div>
      </div>
    </div>
  );
}

function ClientCommunicationHistory({ phoneNumber, allPhones, clientName, clientId }) {
  const [calls, setCalls] = useState([]);
  const [sms, setSms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'calls', 'sms'
  const [expandedCall, setExpandedCall] = useState(null);
  const [stats, setStats] = useState({ calls: 0, sms: 0, nrp: 0 });

  useEffect(() => {
    loadHistory();
  }, [phoneNumber, clientName]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      let callsData = [];
      let smsData = [];
      
      // Search by ALL known phone numbers (CRM + Sheet)
      const phonesToSearch = allPhones && allPhones.length > 0 ? allPhones : (phoneNumber ? [phoneNumber] : []);
      
      if (phonesToSearch.length > 0) {
        const callPromises = phonesToSearch.map(p => 
          axios.get(`${API}/ringover/calls/phone/${encodeURIComponent(p)}`).catch(() => ({ data: { calls: [] } }))
        );
        const smsPromises = phonesToSearch.map(p =>
          axios.get(`${API}/ringover/sms/phone/${encodeURIComponent(p)}`).catch(() => ({ data: { messages: [] } }))
        );
        const [callResults, smsResults] = await Promise.all([
          Promise.all(callPromises),
          Promise.all(smsPromises),
        ]);
        
        // Merge and deduplicate by cdr_id / message id
        const seenCalls = new Set();
        for (const res of callResults) {
          for (const call of (res.data.calls || [])) {
            if (!seenCalls.has(call.cdr_id)) {
              seenCalls.add(call.cdr_id);
              callsData.push(call);
            }
          }
        }
        const seenSms = new Set();
        for (const res of smsResults) {
          for (const msg of (res.data.messages || [])) {
            const msgId = msg.id || `${msg.timestamp}_${msg.from}`;
            if (!seenSms.has(msgId)) {
              seenSms.add(msgId);
              smsData.push(msg);
            }
          }
        }
      }
      
      // Fallback: search by client name
      if (callsData.length === 0 && clientName) {
        try {
          const callsByNameRes = await axios.get(`${API}/ringover/calls/contact/${encodeURIComponent(clientName)}`);
          callsData = callsByNameRes.data.calls || [];
        } catch (e) {
          console.log("Search by name not available");
        }
      }
      
      setCalls(callsData);
      setSms(smsData);
      setStats({
        calls: callsData.length,
        sms: smsData.length,
        nrp: callsData.filter(c => c.is_nrp).length
      });
    } catch (err) {
      console.error("Error loading communication history:", err);
    }
    setLoading(false);
  };

  // Combine and sort by date
  const timeline = [...calls.map(c => ({ ...c, type: 'call', date: c.start_time })),
                    ...sms.map(s => ({ ...s, type: 'sms', date: s.timestamp }))]
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const filteredItems = activeTab === 'all' ? timeline :
                        activeTab === 'calls' ? timeline.filter(i => i.type === 'call') :
                        timeline.filter(i => i.type === 'sms');

  if (!phoneNumber) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Aucun numéro de téléphone renseigné</p>
        <p className="text-sm mt-1">Ajoutez un numéro dans la fiche client pour voir l'historique</p>
      </div>
    );
  }

  // Get calls with AI summaries for quick history
  const callsWithSummaries = calls.filter(c => c.ai_summary);

  return (
    <div className="h-full flex flex-col">
      {/* Quick History - AI Summaries */}
      {callsWithSummaries.length > 0 && (
        <div className="p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-b border-blue-500/30">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-blue-400" />
            <h3 className="font-bold text-white">📋 Historique Rapide</h3>
            <span className="text-xs bg-blue-500/30 text-blue-300 px-2 py-0.5 rounded-full">
              {callsWithSummaries.length} résumé{callsWithSummaries.length > 1 ? 's' : ''} IA
            </span>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {callsWithSummaries.map((call, idx) => (
              <div 
                key={call.cdr_id || idx} 
                className="p-2 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-blue-500/30 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-slate-400">
                    {formatDate(call.start_time)}
                  </span>
                  <span className="text-xs text-slate-500">•</span>
                  <span className="text-xs text-green-400">
                    {formatDuration(call.incall_duration)}
                  </span>
                  <span className="text-xs text-slate-500">•</span>
                  <span className="text-xs text-slate-400">
                    {call.crm_telepro || call.ringover_user_name || "?"}
                  </span>
                </div>
                <p className="text-sm text-white/90 line-clamp-2">{call.ai_summary}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header with stats */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-400" />
              Historique Communications
            </h3>
            <p className="text-sm text-slate-400">
              {allPhones && allPhones.length > 1 
                ? allPhones.map((p, i) => <span key={p}>{i > 0 && ' · '}<span className={i === 0 ? 'text-green-400' : ''}>{p}</span></span>)
                : phoneNumber
              }
            </p>
          </div>
          <button 
            onClick={loadHistory}
            className="p-2 hover:bg-slate-700 rounded-lg"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Mini stats */}
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-green-400" />
            <span className="text-white font-medium">{stats.calls}</span>
            <span className="text-slate-400 text-sm">appels</span>
          </div>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            <span className="text-white font-medium">{stats.sms}</span>
            <span className="text-slate-400 text-sm">SMS</span>
          </div>
          {stats.nrp > 0 && (
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 font-medium">{stats.nrp}</span>
              <span className="text-slate-400 text-sm">NRP</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-700">
        {[
          { id: 'all', label: 'Tout', count: timeline.length },
          { id: 'calls', label: 'Appels', count: calls.length },
          { id: 'sms', label: 'SMS', count: sms.length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <p>Aucune communication trouvée</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {filteredItems.map((item, idx) => (
              item.type === 'call' ? (
                <CallItem 
                  key={`call-${item.cdr_id || idx}`}
                  call={item}
                  expanded={expandedCall === item.cdr_id}
                  onToggle={() => setExpandedCall(expandedCall === item.cdr_id ? null : item.cdr_id)}
                />
              ) : (
                <SmsItem key={`sms-${item.message_id || idx}`} sms={item} />
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientCommunicationHistory;
